import { ChangeEvent, useDeferredValue, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Settings } from '@mint-lib/icons';
import { typographyVariants } from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { GlossaryItem } from '../../api/types.js';
import { DISABLED_CHANNELS } from '../../constants/channelsGlossary.js';
import { useChannelsCatalog } from '../../queries/index.js';
import { EmptyState } from '../atoms/EmptyState.jsx';
import { Icon } from '../atoms/Icon.jsx';
import type {
  ChannelGlossaryOptionsGroup,
  ChannelGlossaryOptionV1,
} from '../types.js';
import { GlossarySelectionListItem } from './GlossarySelectionListItem.jsx';

type ChannelsGlossaryListV1Props = {
  glossaries: GlossaryItem[];
  selectedItems: string[];
  onItemSelect: (itemName: string) => () => void;
};

export const ChannelsGlossaryListV1 = ({
  glossaries,
  selectedItems,
  onItemSelect,
}: ChannelsGlossaryListV1Props) => {
  const { t } = useTranslation('@myn/import-hub');

  const [searchValue, setSearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };
  const handleSearchClear = () => {
    setSearchValue('');
  };

  const {
    data: { channelsByName },
  } = useChannelsCatalog();

  const filteredChannelsList = Object.keys(channelsByName).reduce(
    (acc, channelName) => {
      const channel = channelsByName[channelName];
      if (
        channel.label
          .trim()
          .toLowerCase()
          .includes(deferredSearchValue.trim().toLowerCase()) &&
        !DISABLED_CHANNELS.includes(channelName)
      ) {
        acc.push({
          name: channelName,
          label: channel.label,
          logo: channel.logo ? (
            <Icon component={channel.logo} />
          ) : (
            <Settings className="fill-icon-primary" />
          ),
          readOnly: glossaries.some(
            (glossary) => glossary.name === channelName,
          ),
          type: channel.type as NonNullable<typeof channel.type>,
        });
      }
      return acc;
    },
    [] as ChannelGlossaryOptionV1[],
  );

  const initialChannelsByType: Record<
    ChannelGlossaryOptionV1['type'],
    ChannelGlossaryOptionsGroup
  > = {
    online: {
      label: 'ONLINE',
      items: [] as ChannelGlossaryOptionV1[],
    },
    offline: {
      label: 'OFFLINE',
      items: [] as ChannelGlossaryOptionV1[],
    },
  };

  const channelsByType = filteredChannelsList.reduce((acc, channel) => {
    acc[channel.type].items.push(channel);

    return acc;
  }, initialChannelsByType);

  const channelGroups = Object.values(channelsByType).filter((channelGroup) =>
    Boolean(channelGroup.items.length),
  );

  return (
    <>
      <SearchBar
        value={searchValue}
        onChange={handleSearchChange}
        onClear={handleSearchClear}
        placeholder={t('Search by name')}
        size="small"
        className="mb-2 min-h-8 w-full"
      />
      <div className="flex h-full flex-col gap-6">
        {channelGroups.map((group) => (
          <div key={group.label}>
            <h5
              className={cn(
                typographyVariants({ variant: 'h5', color: 'secondary' }),
                'mb-3',
              )}
            >
              {group.label}
            </h5>
            <div className="flex flex-col gap-2">
              {group.items.map(({ name, label, logo, readOnly }) => (
                <GlossarySelectionListItem
                  key={name}
                  glossaryType="channel"
                  label={label}
                  icon={logo}
                  readOnly={readOnly}
                  selected={selectedItems.includes(name)}
                  onSelect={onItemSelect(name)}
                />
              ))}
            </div>
          </div>
        ))}
        {deferredSearchValue.length && !channelGroups.length ? (
          <div className="flex h-full flex-col justify-center">
            <EmptyState
              icon="IllustrationNoResults"
              text={t('No results found for "{{searchValue}}"', {
                searchValue: deferredSearchValue,
              })}
              description={t('Please, try searching for something different.')}
              className="border-none shadow-none"
            />
          </div>
        ) : null}
      </div>
    </>
  );
};
